import { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";
import { BRAND_COLORS } from "../lib/bookingStatus";

// 23 Sep 2026 · Dezenter „Zum Startbildschirm hinzufügen"-Hinweis.
// Android/Chrome: fängt `beforeinstallprompt` ab → Ein-Klick-Installation.
// iOS/Safari: kein Event → zeigt die manuelle Teilen-Anleitung.
// Wird nie im bereits installierten (standalone) Modus gezeigt und ist
// dauerhaft wegklickbar (localStorage).
const DISMISS_KEY = "wb.install_hint_dismissed";

function isStandalone() {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

function isIOS() {
  try {
    const ua = window.navigator.userAgent || "";
    return /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    let dismissed = false;
    try { dismissed = localStorage.getItem(DISMISS_KEY) === "1"; } catch { /* ignore */ }
    if (dismissed) return undefined;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setIosHint(false);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => { setIosHint(true); setShow(true); }, 1500);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch { /* ignore */ }
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-md bottom-[calc(env(safe-area-inset-bottom)+88px)] md:bottom-6"
    >
      <div
        className="flex items-center gap-3 bg-white border border-navy/15 shadow-xl px-4 py-3"
        style={{ borderRadius: 2 }}
      >
        <img src="/weissblom-logo.webp" alt="" className="w-8 h-8 object-contain shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-navy-dark">Zum Startbildschirm hinzufügen</div>
          {iosHint ? (
            <div className="text-[11px] text-navy-dark/60 leading-snug flex items-center gap-1 flex-wrap">
              Tippen Sie auf <Share className="inline w-3 h-3" strokeWidth={1.8} /> und dann „Zum Home-Bildschirm".
            </div>
          ) : (
            <div className="text-[11px] text-navy-dark/60 leading-snug">Startet wie eine App – auch offline.</div>
          )}
        </div>
        {!iosHint && (
          <button
            onClick={install}
            data-testid="install-prompt-add"
            className="shrink-0 text-[11px] tracking-[0.12em] uppercase text-ivory px-3 py-2 hover:opacity-90 transition-opacity"
            style={{ background: BRAND_COLORS.navy }}
          >
            <span className="inline-flex items-center gap-1"><Plus className="w-3 h-3" strokeWidth={2} /> Hinzufügen</span>
          </button>
        )}
        <button
          onClick={dismiss}
          data-testid="install-prompt-dismiss"
          aria-label="Schließen"
          className="shrink-0 text-navy/40 hover:text-navy p-1 transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );
}
